import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { tokenStorage } from '@lib/auth/storage';
import { looksLikeHtml } from './isHtmlResponse';

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

/**
 * Single shared axios instance. Points at same origin (behind Vite proxy in dev,
 * behind Express static in prod), so relative `/api/...` URLs continue to work.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: '/',
  headers: { 'Content-Type': 'application/json' },
});

/** Device API (port 3087 in prod). Used by Phase 5 onwards. */
export const deviceApiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_DEVICE_API_BASE || '/',
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Reports API (Phase 9). Same origin, but with a 20-minute timeout matching the
 * server-side `req/res.setTimeout` in `timmy-attendance-api/server.js:111-115`.
 * Heavy aggregations on `/api/reports/*` can legitimately take minutes.
 */
export const reportsApiClient: AxiosInstance = axios.create({
  baseURL: '/',
  headers: { 'Content-Type': 'application/json' },
  timeout: 20 * 60 * 1000,
});

const attachAuthHeader = (config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccessToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

/**
 * Tell the API which language the UI is currently showing.
 *
 * Without this the backend resolves the request language from the *browser's*
 * `Accept-Language` (see `timmy-attendance-api/middlewares/i18n.js` →
 * `resolveLng`), so a user whose browser is Arabic kept receiving Arabic
 * messages, status names and report labels even after switching the app to
 * English — the reported "Arabic still shows in English mode" bug.
 *
 * We deliberately send the bare 2-letter code (`en` / `ar`) with no region or
 * q-values. Browsers never do that, so the backend can tell an explicit app
 * choice apart from a passive browser default and keep legacy HTML clients on
 * their current behaviour.
 */
const attachLanguageHeader = (config: InternalAxiosRequestConfig) => {
  // `document.documentElement.lang` is written by the i18n bootstrap on every
  // `languageChanged` (see lib/i18n/index.ts), so it always mirrors what the
  // user is actually looking at. localStorage is the fallback for the very
  // first paint; anything else means English.
  let lng = '';
  try {
    lng = document.documentElement.getAttribute('lang') || localStorage.getItem('i18nextLng') || '';
  } catch {
    lng = '';
  }
  const code = lng.toLowerCase().startsWith('ar') ? 'ar' : 'en';
  if (config.headers) config.headers['Accept-Language'] = code;
  return config;
};

/**
 * For multipart uploads the browser must set `Content-Type` so the
 * `boundary=...` parameter is included. Any explicit value (including the
 * client default `application/json` or a manual `multipart/form-data` without
 * boundary) breaks multer parsing on the server. Strip the header for FormData
 * bodies so the browser sets it correctly.
 */
const fixMultipartContentType = (config: InternalAxiosRequestConfig) => {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (config.headers) {
      delete (config.headers as Record<string, unknown>)['Content-Type'];
      delete (config.headers as Record<string, unknown>)['content-type'];
    }
  }
  return config;
};

apiClient.interceptors.request.use(attachAuthHeader);
apiClient.interceptors.request.use(attachLanguageHeader);
apiClient.interceptors.request.use(fixMultipartContentType);
deviceApiClient.interceptors.request.use(attachAuthHeader);
deviceApiClient.interceptors.request.use(attachLanguageHeader);
deviceApiClient.interceptors.request.use(fixMultipartContentType);
reportsApiClient.interceptors.request.use(attachAuthHeader);
reportsApiClient.interceptors.request.use(attachLanguageHeader);
reportsApiClient.interceptors.request.use(fixMultipartContentType);

type RefreshSubscriber = (token: string | null) => void;
let isRefreshing = false;
let refreshQueue: RefreshSubscriber[] = [];

function resolveQueue(token: string | null) {
  refreshQueue.forEach((cb) => cb(token));
  refreshQueue = [];
}

async function performRefresh(): Promise<string | null> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) return null;
  try {
    const resp = await axios.post('/api/users/refresh-token', { refreshToken });
    const data = resp.data as { accessToken?: string; expiresIn?: number };
    if (data.accessToken) {
      localStorage.setItem('authToken', data.accessToken);
      if (data.expiresIn) {
        localStorage.setItem('tokenExpiry', String(Date.now() + data.expiresIn * 1000));
      }
      return data.accessToken;
    }
    return null;
  } catch {
    return null;
  }
}

let onForcedLogout: (reason: 'session_terminated' | 'refresh_failed') => void = () => {
  tokenStorage.clear();
  const loginPath = '/app/login';
  if (window.location.pathname !== loginPath) window.location.assign(loginPath);
};

export function setForcedLogoutHandler(handler: typeof onForcedLogout) {
  onForcedLogout = handler;
}

function installResponseInterceptor(client: AxiosInstance) {
  client.interceptors.response.use(
    (r) => {
      // Guard: if an API request resolved to the SPA HTML shell (index.html)
      // instead of JSON, reject it so TanStack Query enters its error state
      // (the ErrorBoundary/error UI) rather than passing HTML to components
      // that spread/iterate it — which throws "object is not iterable".
      const contentType = r.headers?.['content-type'];
      if (looksLikeHtml(r.data, typeof contentType === 'string' ? contentType : undefined)) {
        return Promise.reject(new Error(`EXPECTED_JSON_GOT_HTML: ${r.config?.url ?? ''}`));
      }
      return r;
    },
    async (error: AxiosError) => {
      const original = error.config as RetriableConfig | undefined;
      const status = error.response?.status;
      const data = error.response?.data as { code?: string } | undefined;

      if (status === 401 && data?.code === 'SESSION_TERMINATED') {
        onForcedLogout('session_terminated');
        return Promise.reject(error);
      }

      if (status === 401 && original && !original._retry && !original.url?.includes('/refresh-token')) {
        original._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshQueue.push((token) => {
              if (!token) return reject(error);
              original.headers.Authorization = `Bearer ${token}`;
              resolve(client(original));
            });
          });
        }

        isRefreshing = true;
        const newToken = await performRefresh();
        isRefreshing = false;
        resolveQueue(newToken);

        if (!newToken) {
          onForcedLogout('refresh_failed');
          return Promise.reject(error);
        }
        original.headers.Authorization = `Bearer ${newToken}`;
        return client(original);
      }

      return Promise.reject(error);
    },
  );
}

installResponseInterceptor(apiClient);
installResponseInterceptor(deviceApiClient);
installResponseInterceptor(reportsApiClient);

let refreshTimer: ReturnType<typeof setInterval> | null = null;

export function startTokenRefreshLoop() {
  if (refreshTimer) return;
  const tick = async () => {
    if (tokenStorage.getAccessToken() && tokenStorage.isExpiring()) {
      const next = await performRefresh();
      if (!next) onForcedLogout('refresh_failed');
    }
  };
  void tick();
  refreshTimer = setInterval(tick, 60_000);
}

export function stopTokenRefreshLoop() {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const r = await apiClient.get<T>(url, config);
  return r.data;
}
export async function apiPost<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const r = await apiClient.post<T>(url, body, config);
  return r.data;
}
export async function apiPut<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const r = await apiClient.put<T>(url, body, config);
  return r.data;
}
export async function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const r = await apiClient.delete<T>(url, config);
  return r.data;
}
export async function apiPatch<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const r = await apiClient.patch<T>(url, body, config);
  return r.data;
}
